import {
  formatoCantidad,
  formatoFijo,
  formatoNumero,
  formatoPorcentaje,
  formatoPrecio,
} from '@/lib/utils/formato-precio';

/**
 * Cómo escribe Comercia los montos y las medidas (maqueta de Melanie): los totales
 * redondos («USD 46.300»), los precios por unidad con centavos («USD 650,00»),
 * el volumen por unidad con tres decimales («0,010 m³») y el peso con uno.
 */

/** «USD 46.300»: totales y costos grandes, sin centavos. */
export const usd = (n: number, moneda = 'USD') => `${moneda} ${formatoCantidad(n)}`;

/** «USD 650,00»: precios por unidad y montos que se suman en una tabla. */
export const usdExacto = (n: number, moneda = 'USD') => `${moneda} ${formatoPrecio(n)}`;

/** «0,010 m³» por unidad; «5,82 m³» para totales. */
export const m3 = (n: number, decimales = 2) => `${formatoFijo(n, decimales)} m³`;

/** «2,1 kg» por unidad; «1.297 kg» para totales. */
export const kg = (n: number, total = false) =>
  total ? `${formatoCantidad(n)} kg` : `${formatoFijo(n, 1)} kg`;

/** «1,3 t». */
export const toneladas = (kgs: number) => `${formatoNumero(kgs / 1000, 1)} t`;

/** «40 × 30 × 8 cm». */
export const medidas = (largo: number, ancho: number, alto: number, unidad = 'cm') =>
  `${formatoNumero(largo, 1)} × ${formatoNumero(ancho, 1)} × ${formatoNumero(alto, 1)} ${unidad}`;

/** «5,9 × 2,35 × 2,39 m»: el contenedor, en metros como en la maqueta. */
export const medidasEnMetros = (largoCm: number, anchoCm: number, altoCm: number) =>
  `${formatoNumero(largoCm / 100, 2)} × ${formatoNumero(anchoCm / 100, 2)} × ${formatoNumero(altoCm / 100, 2)} m`;

/** «+19,1 %» contra el FOB. */
export const sobreElFob = (pct: number) => formatoPorcentaje(pct, { signo: true });

/** El volumen de una unidad, desde sus medidas en cm. */
export const volumenDe = (largo: number, ancho: number, alto: number) =>
  (largo * ancho * alto) / 1_000_000;

/** Las unidades de «Agregar producto», con su sigla como en la maqueta. */
export const UNIDADES = [
  { valor: 'UND', sigla: 'UND', nombre: 'Unidades' },
  { valor: 'BOX', sigla: 'CJ', nombre: 'Cajas' },
  { valor: 'SET', sigla: 'JGO', nombre: 'Juegos' },
  { valor: 'PAR', sigla: 'PAR', nombre: 'Pares' },
  { valor: 'ROLL', sigla: 'RLL', nombre: 'Rollos' },
] as const;

export const siglaDeUnidad = (valor: string) =>
  UNIDADES.find((u) => u.valor === valor)?.sigla ?? valor;

/**
 * Los colores del plano de carga (maqueta: doce). Bien distintos entre sí para
 * que dos productos vecinos en el contenedor no se lean como uno.
 */
export const COLORES_DE_CARGA = [
  '#3B82F6',
  '#EF4444',
  '#22C55E',
  '#F59E0B',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#F97316',
  '#84CC16',
  '#6366F1',
  '#14B8A6',
  '#D946EF',
] as const;

/** El siguiente color libre, para que cada producto nuevo arranque con uno distinto. */
export function colorSiguiente(usados: string[]): string {
  const libres = COLORES_DE_CARGA.filter(
    (c) => !usados.some((u) => u.toLowerCase() === c.toLowerCase())
  );
  return libres[0] ?? COLORES_DE_CARGA[usados.length % COLORES_DE_CARGA.length];
}

/** Nombre de un color, para el lector de pantalla. */
export const NOMBRE_DE_COLOR: Record<string, string> = {
  '#3B82F6': 'azul',
  '#EF4444': 'rojo',
  '#22C55E': 'verde',
  '#F59E0B': 'ámbar',
  '#8B5CF6': 'violeta',
  '#EC4899': 'rosado',
  '#06B6D4': 'celeste',
  '#F97316': 'naranja',
  '#84CC16': 'lima',
  '#6366F1': 'índigo',
  '#14B8A6': 'turquesa',
  '#D946EF': 'fucsia',
};
